import React, { useEffect, useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, unwrap } from "@/lib/api";

const REASONS = [
  { value: "requested_by_customer", label: "Requested by customer" },
  { value: "duplicate_booking", label: "Duplicate booking" },
  { value: "service_not_provided", label: "Service not provided" },
  { value: "other", label: "Other" },
];

export default function RefundModal({ open, onClose, booking, onRefunded }) {
  const maxAmount = booking?.payment?.amount_paid || booking?.pricing?.total || 0;
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("requested_by_customer");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount(maxAmount ? maxAmount.toFixed(2) : "");
      setReason("requested_by_customer");
      setNotes("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, booking?.id]);

  const confirmRefund = async () => {
    const parsed = parseFloat(amount);
    if (!parsed || parsed <= 0) { toast.error("Enter a valid amount"); return; }
    if (parsed > maxAmount + 0.01) { toast.error(`Amount cannot exceed $${maxAmount.toFixed(2)}`); return; }
    setSubmitting(true);
    try {
      await api.post(`/admin/bookings/${booking.id}/refund`, { amount: parsed, reason, notes });
      toast.success(`Refund of $${parsed.toFixed(2)} processed successfully`);
      onRefunded?.();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail?.message || "Refund failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="refund-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tighter">Refund booking {booking?.booking_number}?</DialogTitle>
          <DialogDescription>This will refund the money to the customer's card via Stripe.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Amount to refund</Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] text-sm">$</span>
              <Input type="number" step="0.01" min="0" max={maxAmount} value={amount}
                onChange={(e) => setAmount(e.target.value)} className="rounded-xl pl-6" data-testid="refund-amount-input" />
            </div>
            <p className="text-xs text-[#9CA3AF] mt-1">Up to ${maxAmount.toFixed(2)} paid</p>
          </div>
          <div>
            <Label className="text-xs">Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="refund-reason-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
              className="rounded-xl mt-1" placeholder="Internal notes about this refund…" data-testid="refund-notes-input" />
          </div>
          <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>This will refund the money to the customer's card via Stripe. The action cannot be undone.</span>
          </div>
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" className="rounded-xl" onClick={onClose} data-testid="refund-cancel-btn">Cancel</Button>
          <Button onClick={confirmRefund} disabled={submitting}
            className="rounded-xl bg-rose-600 hover:bg-rose-700 text-white" data-testid="refund-confirm-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Confirm refund
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
